/**
 * 业绩目标管理页（admin / director / manager 都用）
 * - admin/director：可看任意团队 + 全公司汇总
 * - manager：只能看/改自己团队的目标
 *
 * 功能：
 *   1. 选择月份 + 选择团队（admin/director） / 自动锁本团队（manager）
 *   2. 表格列出团队成员：本月目标 / 实际 / 完成率 / 还差
 *   3. 每行可编辑目标 → 批量保存
 *   4. 一键"应用模板"按 probation 等级填默认值
 *   5. 一键"复制上月"
 */
import { useState, useEffect } from 'react';
import { message, InputNumber, Select } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { $ } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { PageHeader, StatsRow, StatCard, Btn, Pill, TableWrap } from '../../components/ui';
import { toast } from '../../lib/toast';

interface MemberRow {
  userId: number;
  realName: string;
  role: string;
  isProbation: boolean;
  probationLevel: number;
  targetAmount: number;
  actualAmount: number;
  completionRate: number;
  remaining: number;
}
interface TeamProgress {
  summary: {
    teamId: number;
    teamName: string;
    year: number; month: number;
    totalTarget: number; totalActual: number;
    memberCount: number;
    hitTargetCount: number;
    laggingCount: number;
  };
  items: MemberRow[];
}

const TEMPLATE_DEFAULTS = {
  probation2: 10000,
  probation1: 30000,
  probation0: 50000,
  regular: 80000,
};

function fmtY(n: number) {
  return '¥' + (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function rateColor(r: number): 'success' | 'warning' | 'danger' | 'default' {
  if (r >= 1) return 'success';
  if (r >= 0.8) return 'success';
  if (r >= 0.5) return 'warning';
  return 'danger';
}

export function AdminSalesTargetsPage() {
  const user = useAuthStore((s) => s.user);
  // 角色门禁交给 <RouteGuard> 在路由层处理，这里不再内联拦截。
  const isManager = user?.role === 'manager';
  const qc = useQueryClient();

  // 月份选择
  const now = dayjs();
  const [year, setYear] = useState(now.year());
  const [month, setMonth] = useState(now.month() + 1);

  // 团队选择（admin/director）
  const { data: teamsList } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => (await $.get<any[]>('/teams')) ?? [],
  });
  // P0-2-followup-3: manager 直接用 JWT 里的 teamId 初始化 —— 不再依赖 /teams 列表
  // 找 managerId === user.id 的曲折逻辑。teamsList 加载有时序，加上 manager 看不到团队
  // 选择器，导致默认 null 永远不被覆盖。直接用 user.teamId 把 manager 锁死在本团队。
  const [teamId, setTeamId] = useState<number | null>(
    isManager && user?.teamId ? Number(user.teamId) : null,
  );
  useEffect(() => {
    if (!isManager && teamsList && !teamId && teamsList.length > 0) {
      setTeamId(Number(teamsList[0].id));
    }
  }, [teamsList, isManager, teamId]);

  // 团队进度
  const { data: progress, error: progressError, isLoading } = useQuery<TeamProgress>({
    queryKey: ['sales-targets/team', teamId, year, month],
    queryFn: async () => {
      if (!teamId) return null as any;
      return $.get(`/sales-targets/team/${teamId}?year=${year}&month=${month}`);
    },
    enabled: !!teamId,
  });

  // 本地编辑 buffer：userId → targetAmount
  const [edits, setEdits] = useState<Record<number, number>>({});
  useEffect(() => { setEdits({}); }, [teamId, year, month]);

  const batchSave = useMutation({
    mutationFn: async (items: Array<{ userId: number; targetAmount: number }>) => {
      return $.post('/sales-targets/batch', { targetYear: year, targetMonth: month, items });
    },
    onSuccess: () => {
      toast.success('已保存');
      setEdits({});
      qc.invalidateQueries({ queryKey: ['sales-targets/team', teamId] });
    },
    onError: (e: any) => toast.error('保存失败：' + (e?.message ?? '未知错误')),
  });

  const applyTemplate = useMutation({
    mutationFn: async () => {
      return $.post('/sales-targets/apply-template', {
        targetYear: year, targetMonth: month, teamId: isManager ? undefined : teamId,
        rules: TEMPLATE_DEFAULTS,
      });
    },
    onSuccess: () => {
      toast.success('模板已应用');
      qc.invalidateQueries({ queryKey: ['sales-targets/team', teamId] });
    },
    onError: (e: any) => toast.error('应用失败：' + (e?.message ?? '未知错误')),
  });

  const cloneFromPrev = useMutation({
    mutationFn: async () => $.post('/sales-targets/clone-from-prev-month'),
    onSuccess: (r: any) => {
      toast.success(`已从 ${r?.fromYear}-${r?.fromMonth} 复制 ${r?.cloned} 条`);
      qc.invalidateQueries({ queryKey: ['sales-targets/team', teamId] });
    },
    onError: (e: any) => toast.error('复制失败：' + (e?.message ?? '未知错误')),
  });

  const handleSaveAll = () => {
    const items = Object.entries(edits)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([uid, v]) => ({ userId: Number(uid), targetAmount: Number(v) }));
    if (items.length === 0) {
      toast.info('没有可保存的修改');
      return;
    }
    batchSave.mutate(items);
  };

  const sum = progress?.summary;
  const completionRate = sum && sum.totalTarget > 0 ? sum.totalActual / sum.totalTarget : 0;

  return (
    <div>
      <PageHeader
        title="业绩目标管理"
        breadcrumb={['HuanKe', '业绩目标']}
        subtitle="设置 / 查看团队成员本月业绩目标 + 实时完成进度"
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <Select
          value={`${year}-${String(month).padStart(2, '0')}`}
          style={{ width: 140 }}
          onChange={(v) => {
            const [y, m] = v.split('-');
            setYear(Number(y)); setMonth(Number(m));
          }}
          options={Array.from({ length: 12 }, (_, i) => {
            const d = dayjs().subtract(i, 'month');
            return {
              value: `${d.year()}-${String(d.month() + 1).padStart(2, '0')}`,
              label: `${d.year()}年${d.month() + 1}月`,
            };
          })}
        />
        {!isManager && (
          <Select
            value={teamId ?? undefined}
            placeholder="选择团队"
            style={{ width: 200 }}
            onChange={(v) => setTeamId(Number(v))}
            options={(teamsList ?? []).map((t: any) => ({ value: Number(t.id), label: t.name }))}
          />
        )}
        <div style={{ flex: 1 }} />
        <Btn kind="default" onClick={() => applyTemplate.mutate()}>
          {applyTemplate.isPending ? '应用中…' : '应用模板'}
        </Btn>
        <Btn kind="default" onClick={() => cloneFromPrev.mutate()}>
          {cloneFromPrev.isPending ? '复制中…' : '复制上月'}
        </Btn>
        <Btn
          kind="primary"
          onClick={handleSaveAll}
          disabled={Object.keys(edits).length === 0}
        >
          {batchSave.isPending ? '保存中…' : `保存${Object.keys(edits).length > 0 ? ` (${Object.keys(edits).length} 项修改)` : ''}`}
        </Btn>
      </div>

      {sum && (
        <StatsRow>
          <StatCard label="团队总目标" value={fmtY(sum.totalTarget)} unit="" />
          <StatCard label="团队总成交" value={fmtY(sum.totalActual)} unit="" valueColor="success" />
          <StatCard label="团队完成率" value={(completionRate * 100).toFixed(1)} unit="%"
                    valueColor={completionRate >= 1 ? 'success' : completionRate >= 0.5 ? 'warning' : 'danger'} />
          <StatCard label="达标 / 落后"
                    value={`${sum.hitTargetCount} / ${sum.laggingCount}`}
                    unit="人"
                    valueColor={sum.laggingCount > 0 ? 'warning' : 'success'} />
        </StatsRow>
      )}

      <TableWrap>
        <table className="ck-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>成员</th>
              <th>等级</th>
              <th style={{ textAlign: 'right' }}>本月目标</th>
              <th style={{ textAlign: 'right' }}>实际成交</th>
              <th style={{ textAlign: 'right' }}>完成率</th>
              <th style={{ textAlign: 'right' }}>还差</th>
              <th>进度</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>加载中…</td></tr>}
            {/* P0-2-followup: 显式区分 "加载中 / 错误 / 无 team / 团队空 / 渲染" 5 态，
                之前 progress?.items 为 undefined 时既不渲染行也不渲染空态 → 完全空 tbody */}
            {!isLoading && progressError && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--danger)' }}>
                加载失败：{(progressError as any)?.message ?? '请稍后重试'}
              </td></tr>
            )}
            {!isLoading && !progressError && !teamId && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)' }}>
                请选择团队
              </td></tr>
            )}
            {progress?.items?.map((m) => {
              const editVal = edits[m.userId] !== undefined ? edits[m.userId] : m.targetAmount;
              const rate = m.completionRate;
              const tone = rateColor(rate);
              return (
                <tr key={m.userId}>
                  <td>
                    <span style={{ fontWeight: 500 }}>{m.realName}</span>
                  </td>
                  <td>
                    {m.isProbation
                      ? <Pill tone="warning">试用 L{m.probationLevel}</Pill>
                      : <Pill tone="success">正式</Pill>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <InputNumber
                      value={editVal}
                      min={0}
                      step={1000}
                      formatter={(v) => `¥${v}`.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}
                      parser={(v) => Number((v ?? '').replace(/[¥,\s]/g, '')) as any}
                      onChange={(v) => setEdits((prev) => ({ ...prev, [m.userId]: Number(v ?? 0) }))}
                      style={{ width: 130 }}
                    />
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtY(m.actualAmount)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Pill tone={tone}>{(rate * 100).toFixed(0)}%</Pill>
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                              color: m.targetAmount === 0 ? 'var(--text-3)'
                                   : m.actualAmount >= m.targetAmount ? 'var(--success)'
                                   : 'var(--text-2)' }}>
                    {/* v1.5 C-1.a #3 修：双条件判达成；target=0 不能算达成 */}
                    {m.targetAmount === 0
                      ? '未设目标'
                      : m.actualAmount >= m.targetAmount
                        ? '✓ 达成'
                        : fmtY(m.remaining)}
                  </td>
                  <td style={{ width: 200 }}>
                    <div style={{ background: 'var(--bg-3)', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${Math.min(100, rate * 100)}%`,
                          height: '100%',
                          background: rate >= 1 ? 'var(--success)' : rate >= 0.5 ? 'var(--warn)' : 'var(--danger)',
                          transition: 'width 0.3s',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && !progressError && progress && (!progress.items || progress.items.length === 0) && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>
                团队暂无销售员
              </td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <div style={{ marginTop: 16, color: 'var(--text-3)', fontSize: 12 }}>
        模板默认值：试用 L2 ¥10,000 / L1 ¥30,000 / L0 ¥50,000 / 正式 ¥80,000。
        每月 1 号 00:00 系统自动从上月复制目标（cron task）。
      </div>
    </div>
  );
}
